import React, { useState, useEffect } from 'react';
import { Modal, Button, Select, Space, Spin, Tag, message, Tooltip, Checkbox, Divider } from 'antd';
import { CalendarOutlined, LeftOutlined, RightOutlined, StarFilled } from '@ant-design/icons';
import { projectAPI, departmentAPI } from '../services/api';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';

const { Option } = Select;

// 프로젝트 상태별 색상
const STATUS_COLORS = {
    planning: '#faad14',
    in_progress: '#1890ff',
    completed: '#52c41a',
    cancelled: '#ff4d4f',
    on_hold: '#722ed1'
};

const STATUS_NAMES = {
    planning: '계획',
    in_progress: '진행',
    completed: '완료',
    cancelled: '취소',
    on_hold: '보류'
};

const ProjectCalendarModal = ({ visible, onCancel }) => {
    const [loading, setLoading] = useState(false);
    const [projects, setProjects] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [currentDate, setCurrentDate] = useState(dayjs());
    
    // 필터 상태 - 체크박스 방식으로 변경
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [allDepartments, setAllDepartments] = useState(true);
    const [allStatuses, setAllStatuses] = useState(true);
    
    // 달력 데이터
    const [calendarData, setCalendarData] = useState({});
    
    // 각 날짜별 선택된 프로젝트 인덱스 관리
    const [selectedProjectIndexes, setSelectedProjectIndexes] = useState({});

    useEffect(() => {
        if (visible) {
            fetchDepartments();
            fetchProjects();
        }
    }, [visible, currentDate, selectedDepartments, selectedStatuses, allDepartments, allStatuses]);

    const fetchDepartments = async () => {
        try {
            const response = await departmentAPI.getAll({ is_active: 'true' });
            setDepartments(response.data.departments || []);
        } catch (error) {
            console.error('부서 조회 오류:', error);
        }
    };

    const fetchProjects = async () => {
        setLoading(true);
        try {
            const params = {};
            
            // 부서 필터 - 체크박스 방식
            if (!allDepartments && selectedDepartments.length > 0) {
                params.department_id = selectedDepartments.join(',');
            }
            
            // 상태 필터 - 체크박스 방식
            if (!allStatuses && selectedStatuses.length > 0) {
                params.status = selectedStatuses.join(',');
            }

            const response = await projectAPI.getAll(params);
            let projectsData = response.data.projects || [];
            
            // 클라이언트 사이드에서 추가 필터링 (API가 다중 값을 지원하지 않는 경우 대비)
            if (!allDepartments && selectedDepartments.length > 0) {
                projectsData = projectsData.filter(project => 
                    selectedDepartments.includes(project.primary_department_id)
                );
            }
            
            if (!allStatuses && selectedStatuses.length > 0) {
                projectsData = projectsData.filter(project => 
                    selectedStatuses.includes(project.status)
                );
            }
            
            // 달력 데이터로 변환
            const calendarDataMap = generateCalendarData(projectsData);
            setCalendarData(calendarDataMap);
            setProjects(projectsData);
            
            // 선택된 프로젝트 인덱스 초기화
            setSelectedProjectIndexes({});
            
        } catch (error) {
            console.error('프로젝트 조회 오류:', error);
            message.error('프로젝트 데이터를 불러오는 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const generateCalendarData = (projectsData) => {
        const calendarMap = {};
        
        projectsData.forEach(project => {
            // 계약일, 시작일, 종료일을 달력에 표시
            const projectEvents = {};
            
            if (project.contract_date) {
                const dateKey = dayjs(project.contract_date).format('YYYY-MM-DD');
                if (!projectEvents[dateKey]) {
                    projectEvents[dateKey] = {
                        ...project,
                        eventTypes: [],
                        eventLabels: []
                    };
                }
                projectEvents[dateKey].eventTypes.push('contract');
                projectEvents[dateKey].eventLabels.push('계약');
            }
            
            if (project.start_date) {
                const dateKey = dayjs(project.start_date).format('YYYY-MM-DD');
                if (!projectEvents[dateKey]) {
                    projectEvents[dateKey] = {
                        ...project,
                        eventTypes: [],
                        eventLabels: []
                    };
                }
                projectEvents[dateKey].eventTypes.push('start');
                projectEvents[dateKey].eventLabels.push('시작');
            }
            
            if (project.end_date) {
                const dateKey = dayjs(project.end_date).format('YYYY-MM-DD');
                if (!projectEvents[dateKey]) {
                    projectEvents[dateKey] = {
                        ...project,
                        eventTypes: [],
                        eventLabels: []
                    };
                }
                projectEvents[dateKey].eventTypes.push('end');
                projectEvents[dateKey].eventLabels.push('종료');
            }
            
            // 현재 월과 같은 날짜만 달력에 추가
            Object.keys(projectEvents).forEach(dateKey => {
                const monthKey = dayjs(dateKey).format('YYYY-MM');
                if (monthKey === currentDate.format('YYYY-MM')) {
                    if (!calendarMap[dateKey]) {
                        calendarMap[dateKey] = [];
                    }
                    
                    // 합쳐진 이벤트 라벨 생성
                    const combinedEvent = {
                        ...projectEvents[dateKey],
                        eventLabel: projectEvents[dateKey].eventLabels.join('/')
                    };
                    
                    calendarMap[dateKey].push(combinedEvent);
                }
            });
        });
        
        return calendarMap;
    };

    // 체크박스 핸들러 함수들
    const handleDepartmentChange = (checkedValues) => {
        setSelectedDepartments(checkedValues);
        setAllDepartments(checkedValues.length === 0);
    };

    const handleStatusChange = (checkedValues) => {
        setSelectedStatuses(checkedValues);
        setAllStatuses(checkedValues.length === 0);
    };

    const handleAllDepartmentsChange = (e) => {
        const checked = e.target.checked;
        setAllDepartments(checked);
        if (checked) {
            setSelectedDepartments([]);
        }
    };

    const handleAllStatusesChange = (e) => {
        const checked = e.target.checked;
        setAllStatuses(checked);
        if (checked) {
            setSelectedStatuses([]);
        }
    };

    // 위험 프로젝트 판별 (종료일이 지났거나 임박한 프로젝트)
    const isRiskProject = (project) => {
        if (!project.end_date) return false;
        
        const endDate = dayjs(project.end_date);
        const today = dayjs();
        const daysUntilEnd = endDate.diff(today, 'day');
        
        // 이미 종료일이 지났거나 7일 이내 종료 예정
        return daysUntilEnd <= 7 && project.status !== 'completed';
    };

    // 월 이동
    const goToPreviousMonth = () => {
        setCurrentDate(prev => prev.subtract(1, 'month'));
    };

    const goToNextMonth = () => {
        setCurrentDate(prev => prev.add(1, 'month'));
    };

    const goToToday = () => {
        setCurrentDate(dayjs());
    };

    // 마우스 스크롤로 프로젝트 페이지 선택 핸들러
    const handleProjectScroll = (event, dateKey, projectsCount) => {
        event.preventDefault();
        event.stopPropagation();
        
        if (projectsCount <= 4) return; // 4개 이하면 스크롤 불필요
        
        const maxPageIndex = Math.ceil(projectsCount / 4) - 1;
        const currentPageIndex = selectedProjectIndexes[dateKey] || 0;
        let nextPageIndex;
        
        if (event.deltaY > 0) {
            // 아래로 스크롤: 다음 페이지
            nextPageIndex = currentPageIndex >= maxPageIndex ? 0 : currentPageIndex + 1;
        } else {
            // 위로 스크롤: 이전 페이지
            nextPageIndex = currentPageIndex <= 0 ? maxPageIndex : currentPageIndex - 1;
        }
        
        setSelectedProjectIndexes(prev => ({
            ...prev,
            [dateKey]: nextPageIndex
        }));
    };

    // 달력 렌더링
    const renderCalendar = () => {
        const startOfMonth = currentDate.startOf('month');
        const endOfMonth = currentDate.endOf('month');
        const startOfWeek = startOfMonth.startOf('week');
        const endOfWeek = endOfMonth.endOf('week');
        
        const days = [];
        let day = startOfWeek;
        
        while (day.isBefore(endOfWeek) || day.isSame(endOfWeek, 'day')) {
            days.push(day);
            day = day.add(1, 'day');
        }
        
        const weeks = [];
        for (let i = 0; i < days.length; i += 7) {
            weeks.push(days.slice(i, i + 7));
        }
        
        return (
            <div className="calendar-grid">
                {/* 요일 헤더 */}
                <div className="calendar-header">
                    {['일', '월', '화', '수', '목', '금', '토'].map((dayName, index) => (
                        <div key={dayName} className={`day-header ${index === 0 ? 'sunday' : index === 6 ? 'saturday' : ''}`}>
                            {dayName}
                        </div>
                    ))}
                </div>
                
                {/* 날짜 그리드 */}
                <div className="calendar-body">
                    {weeks.map((week, weekIndex) => (
                        <div key={weekIndex} className="calendar-week">
                            {week.map((day, dayIndex) => {
                                const dateKey = day.format('YYYY-MM-DD');
                                const dayProjects = calendarData[dateKey] || [];
                                const isToday = day.isSame(dayjs(), 'day');
                                const isCurrentMonth = day.isSame(currentDate, 'month');
                                
                                return (
                                    <div 
                                        key={dayIndex} 
                                        className={`calendar-cell ${isToday ? 'today' : ''} ${!isCurrentMonth ? 'other-month' : ''}`}
                                    >
                                        <div className={`date-number ${dayIndex === 0 ? 'sunday' : dayIndex === 6 ? 'saturday' : ''}`}>
                                            <span>{day.format('D')}</span>
                                            {dayProjects.length > 0 && (
                                                <span style={{
                                                    fontSize: 8,
                                                    backgroundColor: dayProjects.length > 4 ? '#ff4d4f' : '#52c41a',
                                                    color: 'white',
                                                    borderRadius: '50%',
                                                    width: 14,
                                                    height: 14,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    marginLeft: 2,
                                                    fontWeight: 'bold'
                                                }}>
                                                    {dayProjects.length}
                                                </span>
                                            )}
                                        </div>
                                        <div 
                                            className="events-container"
                                            onWheel={(e) => handleProjectScroll(e, dateKey, dayProjects.length)}
                                            style={{ 
                                                cursor: dayProjects.length > 4 ? 'pointer' : 'default',
                                                position: 'relative'
                                            }}
                                        >
                                            {dayProjects.length > 0 && (() => {
                                                const pageIndex = selectedProjectIndexes[dateKey] || 0;
                                                const startIndex = pageIndex * 4;
                                                const endIndex = Math.min(startIndex + 4, dayProjects.length);
                                                const visibleProjects = dayProjects.slice(startIndex, endIndex);
                                                const totalPages = Math.ceil(dayProjects.length / 4);
                                                
                                                return (
                                                    <>
                                                        {/* 페이지 인디케이터 */}
                                                        {dayProjects.length > 4 && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: -8,
                                                                right: -4,
                                                                background: 'rgba(0,0,0,0.7)',
                                                                color: 'white',
                                                                borderRadius: 8,
                                                                padding: '1px 4px',
                                                                fontSize: 8,
                                                                fontWeight: 'bold',
                                                                zIndex: 20,
                                                                lineHeight: 1
                                                            }}>
                                                                {startIndex + 1}-{endIndex}/{dayProjects.length}
                                                            </div>
                                                        )}
                                                        
                                                        {/* 프로젝트 뱃지들 */}
                                                        {visibleProjects.map((project, index) => {
                                                            const isRisk = isRiskProject(project);
                                                            const actualIndex = startIndex + index;
                                                            
                                                            return (
                                                                <Tooltip 
                                                                    key={`${project.id}-${project.eventTypes?.join('-')}-${actualIndex}`}
                                                                    title={
                                                                        <div>
                                                                            <div style={{ fontWeight: 'bold', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                                <span style={{
                                                                                    backgroundColor: '#1890ff',
                                                                                    color: 'white',
                                                                                    borderRadius: '50%',
                                                                                    width: 18,
                                                                                    height: 18,
                                                                                    fontSize: 10,
                                                                                    fontWeight: 'bold',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    flexShrink: 0
                                                                                }}>
                                                                                    {actualIndex + 1}
                                                                                </span>
                                                                                {project.is_priority && <StarFilled style={{ color: '#ff4d4f', fontSize: 12 }} />}
                                                                                <span>{project.name}</span>
                                                                            </div>
                                                                            <div>현재상태: {STATUS_NAMES[project.status]}</div>
                                                                            <div>부서: {project.primary_department?.name}</div>
                                                                            <div>WBS: {project.eventLabel}</div>
                                                                            {dayProjects.length > 4 && (
                                                                                <div style={{ marginTop: 8, fontSize: 11, color: '#999' }}>
                                                                                    페이지 {pageIndex + 1}/{totalPages} - 스크롤로 전환
                                                                                </div>
                                                                            )}
                                                                            {project.eventTypes?.includes('end') && isRisk && (
                                                                                <div style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                                                                                    ⚠️ 위험 프로젝트
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    }
                                                                    placement="top"
                                                                >
                                                                    <div
                                                                        className={`project-badge ${isRisk && project.eventTypes?.includes('end') ? 'risk-project' : ''}`}
                                                                        style={{
                                                                            backgroundColor: STATUS_COLORS[project.status],
                                                                            borderLeft: project.is_priority ? '3px solid #ff4d4f' : 'none',
                                                                            '--status-color': STATUS_COLORS[project.status],
                                                                            '--risk-color': '#ff4d4f',
                                                                            marginBottom: 1
                                                                        }}
                                                                    >
                                                                        <span className="badge-text">
                                                                            <span style={{
                                                                                backgroundColor: 'rgba(255,255,255,0.9)',
                                                                                color: STATUS_COLORS[project.status],
                                                                                borderRadius: '50%',
                                                                                width: 12,
                                                                                height: 12,
                                                                                fontSize: 8,
                                                                                fontWeight: 'bold',
                                                                                display: 'inline-flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                marginRight: 3,
                                                                                flexShrink: 0
                                                                            }}>
                                                                                {actualIndex + 1}
                                                                            </span>
                                                                            {project.is_priority && <StarFilled style={{ fontSize: 8, marginRight: 2 }} />}
                                                                            {project.name.length > 4 ? `${project.name.substring(0, 4)}...` : project.name}
                                                                        </span>
                                                                        <span className="badge-event-type">
                                                                            {project.eventLabel}
                                                                        </span>
                                                                    </div>
                                                                </Tooltip>
                                                            );
                                                        })}
                                                        
                                                        {/* 추가 프로젝트 표시 */}
                                                        {dayProjects.length > endIndex && (
                                                            <div style={{
                                                                fontSize: 9,
                                                                color: '#999',
                                                                textAlign: 'center',
                                                                marginTop: 2,
                                                                fontStyle: 'italic'
                                                            }}>
                                                                +{dayProjects.length - endIndex}개 더
                                                            </div>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
                
                {/* 스타일 */}
                <style jsx>{`
                    .calendar-grid {
                        border: 1px solid #f0f0f0;
                        border-radius: 6px;
                        overflow: hidden;
                    }
                    .calendar-header {
                        display: grid;
                        grid-template-columns: repeat(7, 1fr);
                        background: #f5f5f5;
                    }
                    .day-header {
                        padding: 8px;
                        text-align: center;
                        font-weight: bold;
                        font-size: 12px;
                        border-right: 1px solid #f0f0f0;
                    }
                    .day-header.sunday { color: #ff4d4f; }
                    .day-header.saturday { color: #1890ff; }
                    .calendar-body {
                        display: flex;
                        flex-direction: column;
                    }
                    .calendar-week {
                        display: grid;
                        grid-template-columns: repeat(7, 1fr);
                        border-bottom: 1px solid #f0f0f0;
                    }
                    .calendar-cell {
                        min-height: 80px;
                        border-right: 1px solid #f0f0f0;
                        padding: 4px;
                        position: relative;
                    }
                    .calendar-cell.today {
                        background: #e6f4ff;
                    }
                    .calendar-cell.other-month {
                        background: #fafafa;
                        opacity: 0.5;
                    }
                    .date-number {
                        font-size: 12px;
                        font-weight: bold;
                        margin-bottom: 2px;
                    }
                    .date-number.sunday { color: #ff4d4f; }
                    .date-number.saturday { color: #1890ff; }
                    .events-container {
                        display: flex;
                        flex-direction: column;
                        gap: 1px;
                        max-height: 60px;
                        overflow: hidden;
                    }
                    .project-badge {
                        font-size: 9px;
                        padding: 1px 3px;
                        border-radius: 3px;
                        color: white;
                        cursor: pointer;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        line-height: 1.2;
                    }
                    .project-badge.risk-project {
                        animation: riskBlink 1.2s infinite ease-in-out;
                        box-shadow: 0 0 8px rgba(255, 77, 79, 0.5);
                    }
                    @keyframes riskBlink {
                        0% { 
                            background-color: var(--status-color) !important;
                            transform: scale(1);
                            box-shadow: 0 0 4px rgba(255, 77, 79, 0.3);
                        }
                        50% { 
                            background-color: var(--risk-color) !important;
                            transform: scale(1.08);
                            box-shadow: 0 0 12px rgba(255, 77, 79, 0.8);
                        }
                        100% { 
                            background-color: var(--status-color) !important;
                            transform: scale(1);
                            box-shadow: 0 0 4px rgba(255, 77, 79, 0.3);
                        }
                    }
                    .badge-text {
                        flex: 1;
                        overflow: hidden;
                        white-space: nowrap;
                        text-overflow: ellipsis;
                    }
                    .badge-event-type {
                        font-size: 7px;
                        opacity: 0.8;
                        margin-left: 2px;
                    }
                `}</style>
            </div>
        );
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CalendarOutlined />
                    <span>프로젝트 진행상황</span>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            footer={[
                <Button key="close" onClick={onCancel}>
                    닫기
                </Button>
            ]}
            width={1000}
            style={{ top: 20 }}
        >
            <div>
                {/* 필터 영역 */}
                <div style={{ marginBottom: 16 }}>
                    {/* 달력 네비게이션 */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        {/*<div style={{ fontWeight: 'bold', fontSize: 16 }}>필터</div>*/}
                        <Space>
                            <Button
                                icon={<LeftOutlined/>}
                                onClick={goToPreviousMonth}
                                size="small"
                            />
                            <Button
                                onClick={goToToday}
                                size="small"
                            >
                                오늘
                            </Button>
                            <Button
                                icon={<RightOutlined/>}
                                onClick={goToNextMonth}
                                size="small"
                            />
                            <span style={{minWidth: 120, textAlign: 'center', fontWeight: 'bold'}}>
                                {currentDate.format('YYYY년 MM월')}
                            </span>

                            <span style={{color: '#ff4d4f', fontSize: 12}}>⚠️ 깜빡임: 위험 프로젝트</span>
                        </Space>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {/* 부서 필터 */}
                        <div>
                            {/*<div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>부서</div>*/}
                            <div style={{ border: '1px solid #f0f0f0', padding: 8, borderRadius: 4 }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                    <Checkbox
                                        checked={allDepartments}
                                        onChange={handleAllDepartmentsChange}
                                        style={{ fontWeight: 'bold' }}
                                    >
                                        전체 부서
                                    </Checkbox>
                                </div>
                                <Divider style={{ margin: '8px 0' }} />
                                <Checkbox.Group
                                    value={selectedDepartments}
                                    onChange={handleDepartmentChange}
                                    style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
                                >
                                    {departments.map(dept => (
                                        <Checkbox key={dept.id} value={dept.id} disabled={allDepartments}>
                                            {dept.name}
                                        </Checkbox>
                                    ))}
                                </Checkbox.Group>
                            </div>
                        </div>

                        {/* 상태 필터 */}
                        <div>
                            {/*<div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>상태</div>*/}
                            <div style={{ border: '1px solid #f0f0f0', padding: 8, borderRadius: 4 }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                    <Checkbox
                                        checked={allStatuses}
                                        onChange={handleAllStatusesChange}
                                        style={{ fontWeight: 'bold' }}
                                    >
                                        전체 상태
                                    </Checkbox>
                                </div>
                                <Divider style={{ margin: '8px 0' }} />
                                <Checkbox.Group
                                    value={selectedStatuses}
                                    onChange={handleStatusChange}
                                    style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
                                >
                                    {Object.entries(STATUS_NAMES).map(([key, name]) => (
                                        <Checkbox key={key} value={key} disabled={allStatuses}>
                                            <Space align="center" size={4}>
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: 12, height: 12,
                                                    borderRadius: 2,
                                                    backgroundColor: STATUS_COLORS[key],
                                                }} />
                                                {name}
                                            </Space>
                                        </Checkbox>
                                    ))}
                                </Checkbox.Group>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/*
                <div style={{ marginBottom: 16, textAlign: 'center' }}>
                    <Space wrap>
                        {Object.entries(STATUS_NAMES).map(([key, name]) => (
                            <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <span style={{
                                    display: 'inline-block',
                                    width: 12, height: 12,
                                    borderRadius: 2,
                                    backgroundColor: STATUS_COLORS[key],
                                }} />
                                <span style={{ fontSize: 12 }}>{name}</span>
                            </span>
                        ))}
                        <span style={{ color: '#bfbfbf', fontSize: 12 }}>│</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <StarFilled style={{ color: '#ff4d4f', fontSize: 12 }} />
                            <span style={{ fontSize: 12 }}>중점관리</span>
                        </span>
                        <span style={{ color: '#bfbfbf', fontSize: 12 }}>│</span>
                        <span style={{ color: '#ff4d4f', fontSize: 12 }}>⚠️ 깜빡임: 위험 프로젝트</span>
                    </Space>
                </div>*/}
                
                {/* 달력 */}
                <Spin spinning={loading}>
                    {renderCalendar()}
                </Spin>
            </div>
        </Modal>
    );
};

export default ProjectCalendarModal;